/* eslint-disable spaced-comment */

interface Analytics {
  track: (
    eventName: string,
    properties: Record<string, string | number | boolean | null | undefined>,
  ) => void;
}

/**
 * Safely track an analytics event
 * @param eventName - The name of the event to track
 * @param properties - The properties to send with the event
 */
export function trackAnalyticsEvent(
  eventName: string,
  properties: Record<string, string | number | boolean | null | undefined>,
): void {
  const analytics = window?.analytics as Analytics | undefined;

  if (analytics) {
    analytics.track(eventName, properties);
  }
}

/******************************************************************************************
 *                                   Analytics Catalog                                    *
 ******************************************************************************************
 *
 * This module catalogs the analytics events we use in recruiter-ui, and exports
 * helpers to maintain an event naming convention. In particular, this module
 * enforces the convention that PostHog recommends here:
 * https://posthog.com/docs/product-analytics/best-practices.
 *
 * Events are broken down into Categories, Objects, and Actions.
 *
 * A Category describes the context an event took place in: `account_settings`,
 * `signup_flow`. Probably smaller than all but the smallest products, might be
 * larger than a page.
 *
 * An Object is a component or location that the event took place in:
 * `forgot_password_button`, `pricing_page`.
 *
 * Categories and Objects in the catalog have associated documentation and owners so
 * that we can identify why they were added and who we have to talk to about
 * changing or removing them.
 *
 * Actions come from a specified list to prevent them from drifting indiscriminately
 */

/**
 * The full set of action suffixes we support.
 *
 * Please add to this list sparingly.
 */
type AnalyticsAction =
  | 'click'
  | 'submit'
  | 'create'
  | 'view'
  | 'add'
  | 'invite'
  | 'update'
  | 'delete'
  | 'remove'
  | 'start'
  | 'end'
  | 'cancel'
  | 'fail'
  | 'generate'
  | 'send'
  | 'copy'
  | 'download'
  | 'change'
  | 'drill'
  | 'open'
  | 'close';

/************** TYPE WIZARDRY **********************************************************************************/
// you are not expected to understand this
// It exists to give you nice errors when you write down the wrong event names

interface AnalyticsObject<T extends string = string> {
  readonly name: T;
  readonly doc: string;
  readonly owner: string;
}

interface AnalyticsCategory<
  T extends string = string,
  Objects extends readonly AnalyticsObject[] = readonly AnalyticsObject[],
> {
  readonly name: T;
  readonly doc: string;
  readonly owner: string;
  readonly objects: Objects;
}

interface AnalyticsCatalog<
  Categories extends readonly AnalyticsCategory[] =
    readonly AnalyticsCategory[],
> {
  readonly categories: Categories;
}

type CategoryForName<
  S extends string,
  Categories extends readonly AnalyticsCategory[],
> = Categories extends readonly []
  ? never
  : Categories extends readonly [infer Category, ...infer Rest]
    ? Category extends AnalyticsCategory
      ? S extends Category['name']
        ? Category
        : Rest extends readonly AnalyticsCategory[]
          ? CategoryForName<S, Rest>
          : never
      : never
    : never;

type ParseAnalyticsEventName<
  S extends string,
  Catalog extends AnalyticsCatalog,
> = S extends `${infer Prefix}_${AnalyticsAction}`
  ? Prefix extends `${infer CategoryName}:${infer ObjectName}`
    ? CategoryName extends Catalog['categories'][number]['name']
      ? CategoryForName<
          CategoryName,
          Catalog['categories']
        > extends AnalyticsCategory<string, infer Objects>
        ? Objects extends readonly AnalyticsObject[]
          ? ObjectName extends Objects[number]['name']
            ? S
            : {
                error: `Object named ${ObjectName} does not belong to category ${CategoryName}`;
              }
          : { error: `No Objects for category ${CategoryName}` }
        : {
            error: `Could not find category name ${CategoryName}`;
          }
      : {
          error: `Provided event name did not use a legal category name`;
        }
    : {
        error: `Provided event name did not have 'Category:Object' format`;
      }
  : {
      error: `Provided event name did not end with one of accepted action suffixes, see: ${AnalyticsAction}`;
    };

/*********** END TYPE WIZARDRY *********************************************************************************/

/*****************************************************************************************
 *                             CARE and KEEPING                                          *
 *                                    of                                                 *
 *                            The EVENT CATALOG                                          *
 *****************************************************************************************
 *
 * 1. Categories don't nest in categories, objects don't nest in objects, so pick your
 *    category and object names carefully
 *
 * 2. Renaming categories and objects will break existing code and make it
 *    hard to track the history of the catalog. Do so sparingly, prefer to leave
 *    notes in docs and create new events with different names
 *
 * 3. Please provide good docs. Tell us how to find a category, tell us where to
 *    look for an object. Tell us why it was added, whether it's intended to keep
 *    it forever or if it's temporary, and what time or under what conditions it
 *    could be removed. Our future selves will thank us.
 *
 * 4. If a category or object has events that stretch across multiple products
 *    or multiple UI areas, don't duplicate them unless totally necessary. We
 *    already track product and UI area on events, so the name doesn't need to
 *    reflect where the event occurred.
 */
export const eventCatalog = {
  categories: [
    {
      name: 'example_workflow',
      doc: 'An example category to illustrate how categories should work',
      owner: 'Hire Example Team',
      objects: [
        {
          name: 'send_now_button',
          doc: 'The button that triggers sending things to people',
          owner: 'Hire Example Team',
        },
        {
          name: 'another_button',
          doc: 'The button that just hangs out being a button',
          owner: 'Hire Example Team',
        },
      ],
    },
    {
      name: 'applicants',
      doc: 'Events on the recruiter Applicants surfaces (list, pipeline, Universal Applicant View)',
      owner: 'Hire',
      objects: [
        {
          name: 'profile',
          doc: 'A single applicant profile opened in the Universal Applicant View (ApplicantDrawerPopup). profile_view measures UAV engagement and the path recruiters take to reach a profile (source property).',
          owner: 'Hire',
        },
        {
          name: 'resume_review',
          doc: 'resume_review_open measures which entry_point drives full-page review use: resume_link or files_tab. resume_review_close measures how a review ends through exit_method: button, escape, drawer_close, or navigation. Both carry a drawer-scoped review_session_id and no applicant personal detail.',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'calendar',
      doc: 'Events related to the Calendar feature, including creating and editing calendar sessions (group interview events)',
      owner: 'Hire',
      objects: [
        {
          name: 'availability_slot',
          doc: 'A calendar availability slot (time slot for a group interview event) managed via /calendar/new and /calendar/:id/edit',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'compliance',
      doc: 'Events for Compliance Stage surfaces in the recruiter app (status labels on the applicant table and drawer)',
      owner: 'Hire',
      objects: [
        {
          name: 'status_label_tooltip',
          doc: 'The per-requirement breakdown tooltip shown when hovering a compliance status label (CMP-1697)',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'merge_keys',
      doc: 'Events for the Merge Keys feature',
      owner: 'Hire',
      objects: [
        {
          name: 'settings',
          doc: 'The Translation Settings page is visited and the Merge Keys component is shown',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'custom_terminology',
      doc: 'Events for the Custom Terminology feature',
      owner: 'Hire',
      objects: [
        {
          name: 'settings',
          doc: 'The Translation Settings page is visited and the Custom Terminologies component is shown',
          owner: 'Hire',
        },
        {
          name: 'upload',
          doc: 'The Custom Terminology CSV file upload flow on the Translation Settings page',
          owner: 'Hire',
        },
        {
          name: 'upload_replace',
          doc: 'The replace file action on the Custom Terminology file preview on the Translation Settings page',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'opening_attributes',
      doc: 'Events on the Opening Attributes (custom fields) settings list. Tracks engagement with the v2.1 usage counts (openings/locations/positions) shown per attribute.',
      owner: 'Hire',
      objects: [
        {
          name: 'edit_button',
          doc: 'The pencil/edit icon on each attribute row in the Opening Attributes list',
          owner: 'Hire',
        },
        {
          name: 'attribute_type',
          doc: 'An attribute type (custom field). Fired when an attribute type is created or deleted via the AttributeTypeForm modal.',
          owner: 'Hire',
        },
        {
          name: 'attribute_name',
          doc: 'The name field of an attribute type. Fired when an attribute name is changed via the AttributeTypeForm modal.',
          owner: 'Hire',
        },
        {
          name: 'attribute_value',
          doc: 'An allowed value of an attribute type. Fired when a value is edited inline or deleted via the AttributeValuesForm.',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'workflows',
      doc: 'Events in the Workflow Editor, including Cue CTAs in the stages sidebar',
      owner: 'Hire',
      objects: [
        {
          name: 'cue_edit_workflow_button',
          doc: 'The "Edit workflow with Cue" CTA in the Workflow Editor stage column header',
          owner: 'Hire',
        },
        {
          name: 'cue_compliance_upsell_button',
          doc: 'The "Discover" CTA on the Compliance upsell promo card shown below an eligible data-collection stage. The card renders only where the Cue panel is mounted, and `view` fires when it mounts. `click` opens the Compliance pitch modal. `close` is the card\'s own Close button, distinct from a view with no click. Property: cta_id.',
          owner: 'Hire',
        },
        {
          name: 'cue_compliance_upsell_modal',
          doc: 'The Compliance pitch modal the card\'s "Discover" CTA opens. `view` and `click` both come from the "Start free trial" CueCtaButton inside it: the modal is mounted only while open, so the button\'s mount is the open, and its click is the dispatch to Cue. `close` is the "Discover Compliance" marketing link or the × / backdrop dismissal, neither of which dispatches; its `action` property (marketing_link, dismiss) is what tells marketing interest from an abandoned pitch. Properties: cta_id, action on close.',
          owner: 'Hire',
        },
        {
          name: 'cue_visualize_workflow_button',
          doc: 'The "Visualize workflow in Cue" menu item in the Workflow Editor nav bar overflow menu (AAI-3308 F6). view fires when the overflow menu opens with the item visible; click fires on selection. Property: cta_id.',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'sourcing',
      doc: 'Events for sourcing / post-to-job-boards entry points (applicant table and Sourcing index)',
      owner: 'Hire Source',
      objects: [
        {
          name: 'post_to_job_boards_popover',
          doc: 'The Freemium Post to Job Boards choice popover (Cue vs manual); entry_point distinguishes applicant table vs sourcing_index',
          owner: 'Hire Source',
        },
        {
          name: 'post_with_cue',
          doc: 'The Post with Cue option in the Post to Job Boards popover',
          owner: 'Hire Source',
        },
        {
          name: 'post_manually',
          doc: 'The Post manually option in the Post to Job Boards popover (navigates to source-ui /post-job)',
          owner: 'Hire Source',
        },
        {
          name: 'go_to_source_button',
          doc: 'The Go to Source CTA on the Freemium landing that replaces Sponsored Postings. Paid sourcing-dashboard-user-access accounts see the landing too, but only source-freemium accounts send the click. Tracks upgrade intent; can be retired when freemium ends.',
          owner: 'Hire Source',
        },
      ],
    },
    {
      name: 'source_post_job',
      doc: 'Launching the Source job posting flow from Hire. source-ui fires the same names from its own entry points, and every event carries product=source.',
      owner: 'Hire Source',
      objects: [
        {
          name: 'button',
          doc: 'The Post to Job Boards button. `button_click` fires when the recruiter picks Post with Cue or Post manually in its popover, as in source-ui, so a dismissed popover sends none; `entry_point` is sponsored_postings on the Sourcing index and opening on an opening page',
          owner: 'Hire Source',
        },
        {
          name: 'flow',
          doc: 'The posting flow. Hire fires `flow_start` only for Post with Cue; a manual posting starts in source-ui, which fires it from the entryPoint on the /post-job link',
          owner: 'Hire Source',
        },
      ],
    },
    {
      name: 'source_sponsored_postings',
      doc: 'The Sponsored Postings page at /:accountSlug/sourcing, the primary Source landing in Hire. A Source account sees the Freemium landing at that route instead, which sends no page_view',
      owner: 'Hire Source',
      objects: [
        {
          name: 'page',
          doc: '`page_view` fires once per landing, after the summary loads, with its spend, cost-per-applicant and applicant totals',
          owner: 'Hire Source',
        },
      ],
    },
    {
      name: 'source_text_to_apply',
      doc: 'The Text to Apply page at /:accountSlug/sourcing/text_to_apply',
      owner: 'Hire Source',
      objects: [
        {
          name: 'page',
          doc: '`page_view` fires once per landing with whether keywords are purchased and whether any are in use',
          owner: 'Hire Source',
        },
      ],
    },
    {
      name: 'source_vonq_contracts',
      doc: 'The VONQ Contracts page at /:accountSlug/sourcing/vonq_contracts',
      owner: 'Hire Source',
      objects: [
        {
          name: 'page',
          doc: "`page_view` fires once per landing with the account's contract count",
          owner: 'Hire Source',
        },
      ],
    },
    {
      name: 'openings',
      doc: 'Events on the Openings list, including the Pool-in-Hire available-talent column',
      owner: 'Pool',
      objects: [
        {
          name: 'cue_campaign_launch_button',
          doc: 'The Email and SMS CTAs in the available-talent tooltip on the Openings list and the Sourcing posting picker, when the pool-cue-campaign-launch flag is on. Opens Cue seeded with a request to draft an invite-to-apply campaign for that opening. Carries `channel` (email or sms), `cta_id`, `opening_id`, `opening_name`, `available_talent_count`, `entry_point` (openings_list or sourcing_posting), `app`, `product` and `is_pool_paid` (true, false, or null while the tier is unknown).',
          owner: 'Pool',
        },
        {
          name: 'campaign_launch_button',
          doc: 'The same Email and SMS CTAs with the pool-cue-campaign-launch flag off, where a click creates a draft campaign and navigates to Pool. Answers how many recruiters start a campaign from the Openings list or the Sourcing posting picker, and on what channel. Carries `channel`, `opening_id`, `opening_name`, `available_talent_count`, `entry_point` (openings_list or sourcing_posting), `app`, `product` and `is_pool_paid`.',
          owner: 'Pool',
        },
        {
          name: 'campaign_draft',
          doc: 'The draft campaign a campaign_launch_button click tries to create. `fail` fires once per failed attempt with `error_type` (forbidden, draft_create_failed, missing_campaign_uuid, network) and `error_status`, the HTTP status of a rejected request (missing_campaign_uuid has none). A failure does not mean no draft exists: Pool can create the draft before the response that failed. Carries the same opening, channel, `entry_point`, `app`, `product` and `is_pool_paid` properties as the click.',
          owner: 'Pool',
        },
      ],
    },
    {
      name: 'candidate_ai_agent',
      doc: 'Events on the Candidate AI Agent page (Fountain AI settings), including the AI agent list and the per-agent conversation analysis report flow',
      owner: 'Hire',
      objects: [
        {
          name: 'conversations',
          doc: 'The Conversations page of one CAI agent. view fires once per page mount with agent_id; filter changes do not fire it.',
          owner: 'Hire',
        },
        {
          name: 'export_report',
          doc: 'The "Analyze conversations" flow for a single AI agent: submit fires on form submission, generate fires once the Hex run has actually been requested (agent_id, range_start, range_end, conversation_count props), cancel and fail cover closing the modal and a failed request respectively',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'scorecards',
      doc: 'Events for applicant scorecards: the fill-out form evaluators reach from a status label or from an emailed link. Answers whether evaluators finish what they open, and how often submission is blocked by unanswered required questions.',
      owner: 'Hire',
      objects: [
        {
          name: 'fill_form',
          doc: 'The shared scorecard fill form. `submit` fires on a successful submission; `blocked` fires when the submit click is stopped by validation. The `context` property separates the applicant-table modal from the standalone evaluator page, and `blocked` carries `missing_count` plus `recommendation_missing`.',
          owner: 'Hire',
        },
        {
          name: 'results_modal',
          doc: 'The scorecard results modal comparing evaluators. `view` fires once the results load, carrying `template_id`, `evaluator_count`, and `submitted_count` — how many columns a viewer compares, and how often the modal opens before everyone is in.',
          owner: 'Hire',
        },
        {
          name: 'invite_modal',
          doc: 'The invite evaluators modal. `view` fires once the assigned list loads, carrying `template_id` and `assigned_count`; `send` fires on a successful invite, carrying `template_id` and `evaluator_count` — how many people scorecards get delegated to beyond the default owner.',
          owner: 'Hire',
        },
        {
          name: 'evaluator_reminder',
          doc: 'The Send reminder action on an evaluator row of the invite modal or of the blocking Scorecard required modal. `send` fires on a successful delivery request, carrying `template_id` and the reminded `evaluator_id` — how often recruiters nudge outstanding evaluators by hand, and whom. The clicking recruiter is the identified person on the event.',
          owner: 'Hire',
        },
        {
          name: 'requirement_blocked_modal',
          doc: 'The blocking Scorecard required modal, shown when the require-scorecard stage setting refuses a move, reject, or bulk action. `view` fires when the modal opens, carrying `stage_id`, `required_submissions`, and `blocked_count` — how often the requirement actually stops a recruiter, and on which threshold.',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'omni',
      doc: 'Events forwarded from the embedded Omni analytics dashboard iframe via postMessage. See https://docs.omni.co/embed/events/consume',
      owner: 'Hire',
      objects: [
        {
          name: 'dashboard',
          doc: 'The full Omni embedded dashboard (download, export)',
          owner: 'Hire',
        },
        {
          name: 'dashboard_filter',
          doc: 'A dashboard filter interaction (changed or applied)',
          owner: 'Hire',
        },
        {
          name: 'dashboard_tile',
          doc: 'A single tile/chart on the dashboard (download, drill)',
          owner: 'Hire',
        },
        {
          name: 'page',
          doc: 'Page-level navigation within the embedded Omni iframe',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'pool',
      doc: 'Events on the Pool Talent surface embedded in the applicant drawer (POOL-2086). wx-system owns the surfaces inside the iframe and shares the `talent_view` name; its events carry `product: pool`. Hire events carry `product: pool` as well, so `app: hire-ui` and `entry_point` (`card` or `messages_banner`) are what tell the two senders apart.',
      owner: 'Pool',
      objects: [
        {
          name: 'talent_card',
          doc: 'The Pool Talent card in the applicant drawer (ApplicantDrawerPoolProfile), shown only when the applicant matches a Pool talent record. Carries `is_pool_paid` (true, false, or null when unknown), `app` and `product`.',
          owner: 'Pool',
        },
        {
          name: 'talent',
          doc: "The read-only Pool talent profile embed (PoolTalentProfileEmbed) opened from the applicant drawer. `entry_point` says which surface opened it: the card or the Messages tab banner. `is_pool_paid` is the applicant account's tier (true, false, or null when unknown), and `app` is `hire-ui`.",
          owner: 'Pool',
        },
      ],
    },
    {
      name: 'offer_approvals',
      doc: 'Events for the in-app offer approval surface: the queue of approvals waiting on the signed-in user and the review panel they open from it (AXHE-3947). Answers two questions the approval engine cannot: whether approvers find the surface at all, and whether opening it leads to a decision. Keep while offer approvals are being rolled out.',
      owner: 'Hire',
      objects: [
        {
          name: 'review_panel',
          doc: 'The right-hand panel showing one offer, its per-level approver status, and the approve/reject pair. Opened from a row of the Approvals tab on /approvals, or on arrival from the deep link in the approver notification mail. The `entry` property says what opened it — `row`, `name`, `preview_link` or `review_button` for a row control, `deep_link` for the mail — so the three affordances Design Requirements §5 adds to an offer row can be told apart from the Review button that was already there, and a view from the mail from one somebody navigated to',
          owner: 'Hire',
        },
        {
          name: 'decision',
          doc: "One approver's approve or reject on one level, recorded from the review panel's footer. The `decision` property says which, and `had_reason` whether a rejection carried the required reason",
          owner: 'Hire',
        },
        {
          name: 'offer_edit',
          doc: 'An approver changing the offer from the review panel rather than deciding it (AXHE-4759). `submit` is the resubmission that restarts the approval; `open` is the form being opened, so the two together say how often an edit is started and abandoned. `from_status` carries the request status it was started from, which is what separates a revise-after-rejection from an edit of a live approval',
          owner: 'Hire',
        },
      ],
    },
  ],
} as const;

export type AnalyticsEvent<S extends string> =
  ParseAnalyticsEventName<S, typeof eventCatalog> extends { error: infer Error }
    ? Error extends string
      ? Error
      : 'Unknown error parsing event name'
    : S;
